import { ColorNeutralWhite, BoxShadowXl, ColorNeutralLightGrey, Spacing16 } from 'autogen/design-tokens/tokens';
import dynamic from 'helpers/dynamic';
import styled, { type RuleSet, css } from 'styled-components';
import { breakpoint, sizes } from 'variables';
import useContainerQuery from 'hooks/responsive/useContainerQuery';
import ScrollSnapSliderContext from '../context/ScrollSnapSliderContext';
import useScrollSnapSlider from '../hooks/useScrollSnapSlider';
import ScrollSnapSlider from '../ScrollSnapSlider';
import NavigationFadeButton from './NavigationFadeButton';
import Icon from 'components/Icon';

const RootContainer = styled.div`
    position: relative;
`;

const StyledScrollSnapSlider = styled(ScrollSnapSlider)`
    scroll-snap-type: none;
    scroll-snap-stop: normal;
    gap: 8px;

    ${breakpoint.up(sizes.xl)} {
        scroll-snap-type: x mandatory;
        scroll-snap-stop: always;
    }

    &::-webkit-scrollbar {
        display: none;
    }
`;

export const commonNavigationButtonStyling = css<{ $isVisible: boolean }>`
    display: none;
    background-color: ${ColorNeutralWhite};
    box-shadow: ${BoxShadowXl};
    border: 1px solid ${ColorNeutralLightGrey};
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 3;

    ${breakpoint.up(sizes.xl)} {
        display: flex;
        ${({ $isVisible }) => $isVisible && 'display: none'}
    }

    ${({ $isVisible }) => $isVisible && 'display: none'}
`;

export const StyledNavigationButtonRight = styled(NavigationFadeButton)<{
    $isVisible: boolean;
    $carouselButtonsIndent?: string;
}>`
    ${commonNavigationButtonStyling}
    right: ${({ $carouselButtonsIndent }) => ($carouselButtonsIndent ? $carouselButtonsIndent : `-${Spacing16}`)};
`;

export const StyledNavigationButtonLeft = styled(NavigationFadeButton)<{
    $isVisible: boolean;
    $carouselButtonsIndent?: string;
}>`
    ${commonNavigationButtonStyling}
    left: ${({ $carouselButtonsIndent }) => ($carouselButtonsIndent ? $carouselButtonsIndent : `-${Spacing16}`)};
`;

const options = {
    draggable: true,
    loop: false
};

type Props<T> = {
    items: T[];
    itemRenderer: (item: T, index: number) => JSX.Element | JSX.Element[];
    hideCarouselButtons?: boolean;
    scrollContainerCss?: RuleSet;
    carouselButtonsIndent?: string;
};

export default function ItemCarousel<T>({
    items,
    itemRenderer,
    hideCarouselButtons = false,
    scrollContainerCss,
    carouselButtonsIndent,
    ...restProps
}: Props<T>) {
    const { ref, slider } = useScrollSnapSlider(options);

    const [containerRef, slideAmount] = useContainerQuery<number>(
        { '0': 1, '544': 2, '768': 3, '992': 4, '1600': 5 },
        1
    );

    return (
        <ScrollSnapSliderContext.Provider value={slider}>
            <RootContainer {...restProps} ref={containerRef}>
                <StyledNavigationButtonRight
                    $isVisible={hideCarouselButtons}
                    navType="next"
                    slideAmount={slideAmount}
                    variant={null}
                    size="lg"
                    $carouselButtonsIndent={carouselButtonsIndent}
                >
                    <Icon type="ChevronRight" size={16} />
                </StyledNavigationButtonRight>

                <StyledNavigationButtonLeft
                    $isVisible={hideCarouselButtons}
                    navType="prev"
                    slideAmount={slideAmount}
                    variant={null}
                    size="lg"
                    $carouselButtonsIndent={carouselButtonsIndent}
                >
                    <Icon type="ChevronLeft" size={16} />
                </StyledNavigationButtonLeft>

                <StyledScrollSnapSlider css={scrollContainerCss} ref={ref}>
                    {items.map((item, index) => itemRenderer(item, index))}
                </StyledScrollSnapSlider>
            </RootContainer>
        </ScrollSnapSliderContext.Provider>
    );
}
